/**
 * Whether the rider's payout on this delivery was checked.
 *
 * Shown to everyone who can read the delivery; set only by owners and admins
 * (`deliveries:verify_payout`). It is a review status and nothing more: saving
 * it changes no fee, payout, share, sale, expense or customer payment, and the
 * screen says so, so nobody mistakes flagging a payout for correcting it.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PAYOUT_VERIFICATION_LABELS,
  PAYOUT_VERIFICATION_STATUSES,
  type Delivery,
  type PayoutVerificationStatus,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { date } from '../../lib/format';
import { Badge, Button, Select, Textarea } from '../../components/ui';

const TONE: Record<PayoutVerificationStatus, 'success' | 'warning' | 'danger'> = {
  verified: 'success',
  under_review: 'warning',
  not_verified: 'danger',
};

export function PayoutVerificationSection({
  delivery,
  timezone,
}: {
  delivery: Delivery;
  timezone: string;
}): ReactNode {
  const { can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const current = delivery.payoutVerification ?? null;

  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<PayoutVerificationStatus>(current?.status ?? 'under_review');
  const [note, setNote] = useState(current?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.post(path(`/deliveries/${delivery.id}/payout-verification`), { status, note: note.trim() || null }),
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['delivery', delivery.id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save. Try again in a moment.'),
  });

  const mayEdit = can('deliveries:verify_payout');

  return (
    <div className="border-t border-slate-100 px-4 py-3" data-testid="payout-verification">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="w-40 shrink-0 text-xs text-slate-500">Rider payout verification</span>
        {current ? (
          <Badge tone={TONE[current.status]}>{PAYOUT_VERIFICATION_LABELS[current.status]}</Badge>
        ) : (
          <span className="text-sm text-slate-500">Not checked</span>
        )}
        {mayEdit && !editing && (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            {current ? 'Change' : 'Set status'}
          </Button>
        )}
      </div>

      {current && (
        <div className="mt-1 space-y-1 pl-0 sm:pl-44">
          {current.note && <p className="whitespace-pre-line text-sm text-slate-900">{current.note}</p>}
          <p className="text-xs text-slate-500">
            {current.setByName ? `Set by ${current.setByName}` : 'Set'}
            {current.setAt ? ` · ${date(current.setAt, timezone)}` : ''}
          </p>
        </div>
      )}

      {editing && (
        <div className="mt-3 space-y-3 sm:pl-44">
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as PayoutVerificationStatus)}
            options={PAYOUT_VERIFICATION_STATUSES.map((s) => ({ value: s, label: PAYOUT_VERIFICATION_LABELS[s] }))}
          />
          <Textarea
            label="Explanation and supporting notes"
            required={status !== 'verified'}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            hint="This records a review only. The fee, the rider's payout and the business's share do not change."
          />
          {error && (
            <p role="alert" className="text-xs font-medium text-red-600">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
